import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SidebarContent = ({ conversations, activeConversation, onNewChat, onSelectConversation, onDeleteConversation }) => (
  <>
    {/* New Chat Button */}
    <div className="p-4 border-b border-gray-200 dark:border-gray-800">
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onNewChat}
        className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl font-medium shadow-lg shadow-purple-500/30 transition-all flex items-center justify-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        گفتگوی جدید
      </motion.button>
    </div>

    {/* Conversations List */}
    <div className="flex-1 overflow-y-auto p-3 space-y-2">
      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 mb-2">تاریخچه گفتگوها</h3>
      {conversations.map((conv, index) => (
        <motion.div
          key={conv.id}
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: index * 0.05 }}
          whileHover={{ scale: 1.02, x: -5 }}
          className={`w-full text-right p-3 rounded-lg transition-all relative group ${
            activeConversation === conv.id
              ? 'bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 border border-purple-300 dark:border-purple-700'
              : 'hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
        >
          <button
            onClick={() => onSelectConversation(conv.id)}
            className="w-full text-right"
          >
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${
                activeConversation === conv.id ? 'bg-purple-600' : 'bg-gray-400'
              }`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {conv.title}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {conv.time}
                </p>
              </div>
            </div>
          </button>
          
          {/* Delete Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteConversation(conv.id);
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
          >
            <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </motion.button>
        </motion.div>
      ))}
    </div>

    {/* Footer Info */}
    <div className="p-4 border-t border-gray-200 dark:border-gray-800">
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>تمامی حقوق برای متافلای محفوظ میباشد</span>
      </div>
    </div>
  </>
);

const Sidebar = ({ conversations, activeConversation, onNewChat, onSelectConversation, onDeleteConversation, isOpen, onClose }) => {
  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:w-80 lg:w-80 xl:w-96 bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 flex-col">
        <SidebarContent
          conversations={conversations}
          activeConversation={activeConversation}
          onNewChat={onNewChat}
          onSelectConversation={onSelectConversation}
          onDeleteConversation={onDeleteConversation}
        />
      </aside>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
            />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="md:hidden fixed right-0 top-14 sm:top-16 bottom-0 w-72 max-w-[85vw] bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 flex flex-col z-50 rounded-l-2xl shadow-2xl"
            >
              <SidebarContent
                conversations={conversations}
                activeConversation={activeConversation}
                onNewChat={() => {
                  onNewChat();
                  onClose();
                }}
                onSelectConversation={(id) => {
                  onSelectConversation(id);
                  onClose();
                }}
                onDeleteConversation={onDeleteConversation}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
